import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowUpRight, Plus, Terminal } from "lucide-react";
import { fmtMs, fmtUsd, preview, timeAgo, useQuery, useStatus } from "../lib";
import { Badge, Button, CopyButton, KindIcon, SourceBadge, Spark, Dot } from "../ui";
import { NewProjectModal } from "./modals";
import type { Execution } from "../../shared/types";

export function Home() {
  const { data: projects } = useQuery<any[]>("/api/projects", { poll: 15_000 });
  const { data: recent } = useQuery<Execution[]>("/api/executions?limit=8&hideEval=1", { poll: 10_000 });
  const { data: keys } = useQuery<any>("/api/keys");
  const status = useStatus();
  const [, nav] = useLocation();
  const [newProject, setNewProject] = useState(false);
  const totals = (projects ?? []).reduce(
    (acc, p) => {
      for (const t of p.targets) {
        acc.targets++;
        if (t.channels?.production) acc.live++;
        acc.req += t.stats?.requests ?? 0;
        acc.cost += t.stats?.costUsd ?? 0;
      }
      return acc;
    },
    { targets: 0, live: 0, req: 0, cost: 0 }
  );
  const curl = `curl ${location.origin}/v1/atendimento/classificar-ticket \\
  -H "Authorization: Bearer ${keys?.local ?? "ak_..."}" \\
  -H "content-type: application/json" \\
  -d '{"inputs": {"ticket": "Me cobraram duas vezes"}}'`;
  return (
    <>
      <div className="topbar">
        <div className="crumbs"><span className="cur">Início</span></div>
      </div>
      <div className="content">
        <div className="page">
          <div className="page-head">
            <div>
              <h1 className="page-title">Ateliê</h1>
              <div className="page-sub">Prompts e agentes versionados, avaliados contra casos de teste e publicados como API no mesmo lugar.</div>
            </div>
            <Button variant="primary" icon={<Plus size={15} />} onClick={() => setNewProject(true)}>Novo projeto</Button>
          </div>

          <div className="grid-4">
            <div className="card stat"><div className="label">Alvos</div><div className="stat-value">{totals.targets}</div><div className="stat-sub">{totals.live} em produção</div></div>
            <div className="card stat"><div className="label">Chamadas 24 h</div><div className="stat-value">{totals.req}</div><div className="stat-sub">API e playground</div></div>
            <div className="card stat"><div className="label">Custo 24 h</div><div className="stat-value">{fmtUsd(totals.cost)}</div><div className="stat-sub">API; assinatura à parte</div></div>
            <div className="card stat"><div className="label">Projetos</div><div className="stat-value">{projects?.length ?? "—"}</div><div className="stat-sub ellipsis">em {status?.home?.replace(/^\/Users\/[^/]+/, "~") ?? "…"}</div></div>
          </div>

          <div className="section">
            <div className="section-head"><h2 className="section-title">Projetos</h2></div>
            <div className="grid-2">
              {(projects ?? []).map((p) => (
                <div key={p.slug} className="card proj-card" onClick={() => nav(`/p/${p.slug}`)}>
                  <div className="between">
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</div>
                      <div className="muted" style={{ marginTop: 3 }}>{p.description}</div>
                    </div>
                    <ArrowUpRight size={16} className="faint" />
                  </div>
                  <div>
                    {p.targets.slice(0, 5).map((t: any) => (
                      <Link key={t.slug} href={`/p/${p.slug}/t/${t.slug}`} className="target-line" onClick={(e) => e.stopPropagation()}>
                        <KindIcon kind={t.kind} />
                        <span className="grow ellipsis">{t.name}</span>
                        {t.stats?.series && <Spark values={t.stats.series} width={64} height={18} />}
                        {t.channels?.production ? <Badge tone="green">prod v{t.channels.production}</Badge> : <Badge>draft</Badge>}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="section grid-2" style={{ alignItems: "start" }}>
            <div className="card">
              <div className="card-head"><span className="card-title">Atividade recente</span><Link href="/usage" className="faint" style={{ fontSize: 12 }}>ver todo o usage</Link></div>
              <table className="table">
                <tbody>
                  {(recent ?? []).map((e) => (
                    <tr key={e.id} className="click" onClick={() => nav(`/p/${e.project}/t/${e.target}/usage?ex=${e.id}`)}>
                      <td style={{ width: 16 }}><Dot tone={e.status === "error" ? "red" : "green"} /></td>
                      <td><div className="ellipsis" style={{ maxWidth: 220 }}>{e.target}</div><div className="faint ellipsis" style={{ fontSize: 12, maxWidth: 240 }}>{e.error ?? (e.outputFormat === "image" ? "imagem gerada" : preview(e.output, 60))}</div></td>
                      <td><SourceBadge source={e.source} /></td>
                      <td className="r mono faint" style={{ fontSize: 12 }}>{e.cacheHit ? "cache" : fmtMs(e.latencyMs)}<div>{timeAgo(e.createdAt)}</div></td>
                    </tr>
                  ))}
                  {recent && !recent.length && (<tr><td className="faint" style={{ padding: 18 }}>Nada executado ainda.</td></tr>)}
                </tbody>
              </table>
            </div>
            <div className="code-card">
              <div className="code-card-head">
                <span className="row label"><Terminal size={13} /> Chame qualquer alvo publicado</span>
                <CopyButton text={curl} label="Copiar" />
              </div>
              <pre>{curl}</pre>
              <div className="card-foot faint" style={{ fontSize: 12 }}>
                O canal vem depois do <span className="mono">@</span> (<span className="mono">@staging</span>, <span className="mono">@v3</span>). Sem canal, usa <span className="mono">production</span>.
              </div>
            </div>
          </div>
        </div>
      </div>
      <NewProjectModal open={newProject} onClose={() => setNewProject(false)} />
    </>
  );
}
